import React, { useCallback, useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { colorPrimary } from "../../../theme";
import {
  anyPass,
  ascend,
  equals,
  find,
  identity,
  map,
  pickBy,
  pipe,
  prop,
  propEq,
  sort,
  tail,
} from "ramda";
import clsx from "clsx";
import {
  formatDuration,
  isAlbum,
  isAudiobook,
  isChapterBook,
  objectDiff,
} from "../../../util/mediaUtil";
import { MediaContext } from "../../common/media-context";
import {
  useChapters,
  useContentUpdate,
  useUpdateContentFiles,
} from "../contentServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notBlank } from "../../../util/ext";
import {
  faCheck,
  faGripLines,
  faPause,
  faPen,
  faPlay,
  faX,
} from "@fortawesome/pro-regular-svg-icons";
import { useSnackAlert } from "../../../common/use-snackAlert";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor,
} from "@dnd-kit/modifiers";
import ReactJWPlayer from "react-jw-player";
import SortableItem from "../../common/dnd/SortableItem";
import {
  mapChildOrder,
  mapSequences,
  reorderItems,
  update,
} from "../../common/dnd/dndUtils";

import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CloseIcon from "@material-ui/icons/Close";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Divider from "@material-ui/core/Divider";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import SnackAlert from "../../../components/SnackAlert";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import SaveButton from "../../../components/SaveButton";

const useStyles = makeStyles(theme => ({
  accordionDetails: {
    padding: theme.spacing(3.5, 0, 0, 0),
  },

  accordionSummary: {
    padding: 0,
    minHeight: "auto",
    "&.Mui-expanded": {
      borderBottomStyle: "dashed",
      borderBottomColor: theme.palette.secondary.light,
      borderBottomWidth: 1,
    },
  },

  accordionSummaryContent: {
    margin: 0,
    "&.Mui-expanded": {
      margin: 0,
      padding: theme.spacing(0, 0, 2.5, 0),
    },
  },

  buttonGridItem: {
    paddingRight: "0 !important",
    paddingLeft: "0 !important",
  },

  chapterRowButton: {
    color: "black",
  },

  closeButton: {
    color: theme.palette.primary.darker,
    fontWeight: "bolder",
  },

  closeButtonIcon: {
    fontSize: "3.2rem",
  },

  dialogActions: {
    padding: theme.spacing(6, 4),
  },

  dialogTitle: {
    paddingBottom: 0,
  },

  divider: {
    margin: theme.spacing(3, 0),
  },

  handle: {
    cursor: "grab",
    borderRadius: 5,
    "&:hover": {
      background: theme.palette.secondary.light,
    },
  },

  handleDragging: {
    cursor: "grabbing",
    background: theme.palette.secondary.light,
  },

  player: {
    "& .jwplayer:not(.jw-breakpoint--1) .jw-text-duration::before, & .jwplayer:not(.jw-breakpoint-0) .jw-text-duration::before": {
      content: "none !important",
    },

    "& .jw-horizontal-volume-container": {
      order: 4,
      width: "140px !important",
    },

    "& .jw-icon-playback, & .jw-icon-rewind": {
      display: "none !important",
    },

    "& .jw-icon-volume": {
      order: 3,
      boxShadow: "none !important",
      "&.jw-open::after": {
        opacity: "0 !important",
      },
    },

    "& .jw-knob": {
      boxShadow: "none !important",
    },

    "& .jw-slider-container": {
      height: "5px !important",
      background: "rgba(0, 0, 0, 0.12) !important",
    },

    "& .jw-slider-time": {
      width: "inherit !important",
    },

    "& .jw-slider-volume": {
      opacity: "1 !important",
    },

    "& .jw-text-duration": {
      order: 2,
    },
  },

  row: {
    padding: theme.spacing(3, 4),
    borderBottomColor: theme.palette.secondary.light,
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
  },

  rowDragging: {
    borderRadius: 5,
    border: "none",
    zIndex: 2,
    boxShadow: "0px 0px 30px rgba(149, 169, 177, 0.3)",
    background: "white",
    cursor: "grabbing",
  },

  saveRowButton: {
    color: theme.palette.brandPrimary,
    fontWeight: 900,
  },
}));

const getSources = (formats, fileName) => {
  const bitRateOrder = ["64k", "192k"];

  const getBitRateOrderValue = bitRate => {
    const index = bitRateOrder.indexOf(bitRate);
    return index === -1 ? bitRateOrder.length : index;
  };

  return pipe(
    map(format => ({
      file: format.url.replace(/content_/, `content/${fileName}_`),
      label: format.bitRate,
    })),
    sources =>
      sources.sort(
        (a, b) => getBitRateOrderValue(a.label) - getBitRateOrderValue(b.label),
      ),
  )(formats);
};

const AudiobookChapterRow = React.memo(function AudiobookChapterRow(props) {
  const {
    chapter,
    handleSampleChange,
    handleTitleChange,
    isDragging,
    listeners,
  } = props;
  const classes = useStyles();
  const context = useContext(MediaContext);
  const [audioSource, setAudioSource] = useState([]);
  useEffect(() => {
    if (notBlank(context.media.audioFormats)) {
      const audioSource = getSources(
        context.media.audioFormats,
        chapter.fileName,
      );
      setAudioSource(audioSource);
    }
  }, [context.media.audioFormats, chapter]);

  const [editMode, setEditMode] = useState(false);
  const handleClickEdit = () => setEditMode(true);
  const handleClickSave = () => setEditMode(false);

  //-- Manage Player State
  const [ready, setReady] = useState(false);
  const handlePlayerReady = () => setReady(true);

  const [playing, setPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const handleOpenAccordion = (event, expanded) => {
    if (ready) {
      setExpanded(!expanded);
      setPlaying(true);
      window.jwplayer(`player-${chapter.id}`).play();
    }
  };

  const handleCloseAccordion = () => {
    window.jwplayer(`player-${chapter.id}`).pause();
    setExpanded(false);
  };

  const handlePlayClick = () => {
    if (ready) {
      setPlaying(!playing);
      const player = window.jwplayer(`player-${chapter.id}`);
      playing ? player.pause() : player.play();
    }
  };

  return (
    <Accordion expanded={expanded} square className={classes.row}>
      <AccordionSummary
        classes={{
          root: classes.accordionSummary,
          content: classes.accordionSummaryContent,
        }}
      >
        <Grid container item alignItems="center" justify="space-between">
          <Grid container item xs={10} spacing={4} alignItems="center">
            <Grid
              item
              className={clsx(classes.handle, {
                [classes.handleDragging]: isDragging,
              })}
              {...listeners}
            >
              <FontAwesomeIcon icon={faGripLines} />
            </Grid>
            <Grid item xs>
              {editMode ? (
                <TextField
                  fullWidth
                  placeholder="Chapter Title"
                  value={chapter.title || ""}
                  onChange={handleTitleChange(chapter.id)}
                  InputProps={{
                    endAdornment: (
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            name={`audiobook-chapter-${chapter.id}`}
                            onChange={handleSampleChange(chapter.id)}
                            checked={Boolean(chapter.isSample)}
                          />
                        }
                        label="Sample"
                      />
                    ),
                  }}
                />
              ) : (
                <Typography style={{ cursor: "default" }}>
                  {chapter.title}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Grid
            item
            container
            xs
            justify="flex-end"
            alignItems="center"
            spacing={4}
          >
            <Grid item>
              <Typography>{formatDuration(chapter.duration)}</Typography>
            </Grid>
            <Grid item className={classes.buttonGridItem}>
              {editMode ? (
                <IconButton
                  className={classes.saveRowButton}
                  onClick={handleClickSave}
                >
                  <FontAwesomeIcon icon={faCheck} />
                </IconButton>
              ) : (
                <IconButton
                  disabled={expanded}
                  onClick={handleClickEdit}
                  className={classes.chapterRowButton}
                >
                  <FontAwesomeIcon icon={faPen} />
                </IconButton>
              )}
            </Grid>
            <Grid item className={classes.buttonGridItem}>
              {expanded ? (
                <IconButton
                  onClick={handlePlayClick}
                  className={classes.chapterRowButton}
                >
                  <FontAwesomeIcon icon={playing ? faPause : faPlay} />
                </IconButton>
              ) : (
                <IconButton
                  disabled={!ready}
                  onClick={handleOpenAccordion}
                  className={classes.chapterRowButton}
                >
                  <FontAwesomeIcon icon={faPlay} />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails classes={{ root: classes.accordionDetails }}>
        <Grid container justify="space-between" alignItems="center">
          <Grid item>
            {notBlank(audioSource) && (
              <ReactJWPlayer
                playerId={`player-${chapter.id}`}
                className={classes.player}
                onReady={handlePlayerReady}
                playerScript="https://cdn.jwplayer.com/libraries/nSGiuJOY.js"
                customProps={{
                  skin: {
                    controlbar: {
                      background: "white",
                      icons: "black",
                      text: "black",
                    },
                    timeslider: {
                      progress: colorPrimary.brandPrimary,
                    },
                  },
                  sources: audioSource,
                  height: 40,
                  width: 670,
                }}
              />
            )}
          </Grid>
          <Grid item>
            <IconButton
              onClick={handleCloseAccordion}
              className={classes.chapterRowButton}
            >
              <FontAwesomeIcon icon={faX} />
            </IconButton>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
});

const BookChapterRow = React.memo(function BookChapterRow(props) {
  const classes = useStyles();
  const { chapter, handleTitleChange, isDragging, listeners } = props;

  const [editMode, setEditMode] = useState(false);
  const handleClickEdit = () => setEditMode(true);
  const handleClickSave = () => setEditMode(false);
  return (
    <Grid
      container
      item
      alignItems="center"
      justify="space-between"
      className={classes.row}
    >
      <Grid container item xs={10} spacing={4} alignItems="center">
        <Grid
          item
          className={clsx(classes.handle, {
            [classes.handleDragging]: isDragging,
          })}
          {...listeners}
        >
          <FontAwesomeIcon icon={faGripLines} />
        </Grid>
        <Grid item xs>
          {editMode ? (
            <TextField
              fullWidth
              placeholder="Chapter Title"
              value={chapter.title || ""}
              onChange={handleTitleChange(chapter.id)}
            />
          ) : (
            <Typography style={{ cursor: "default" }}>
              {chapter.title}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Grid
        item
        container
        xs
        justify="flex-end"
        alignItems="center"
        spacing={4}
      >
        <Grid item className={classes.buttonGridItem}>
          {editMode ? (
            <IconButton
              className={classes.saveRowButton}
              onClick={handleClickSave}
            >
              <FontAwesomeIcon icon={faCheck} />
            </IconButton>
          ) : (
            <IconButton
              onClick={handleClickEdit}
              className={classes.chapterRowButton}
            >
              <FontAwesomeIcon icon={faPen} />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
});

const EditChaptersDialog = React.memo(function EditChaptersDialog(props) {
  const { onCancel, onClose, open } = props;
  const classes = useStyles();

  //-- Manage the alert message
  const snackAlertHandler = useSnackAlert();
  const { showAlert } = snackAlertHandler;

  const context = useContext(MediaContext);
  const files = useChapters(context.media.id, context.media.type);
  const [chapters, setChapters] = useState([]);
  const [initialChapters, setInitialChapters] = useState([]);
  useEffect(() => {
    if (files.isError) {
      showAlert("error", [files.error.message]);
    }

    if (files.isSuccess) {
      setChapters(files.data);
      setInitialChapters(files.data);
    }
  }, [files.isSuccess, files.isError, files.data, files.error, showAlert]);

  const handleCancel = () => {
    onCancel();
    setChapters(initialChapters);
  };

  const handleClose = () => {
    onClose();
    setChapters(initialChapters);
  };

  const handleTitleChange = id => event => {
    const title = event.target.value;
    setChapters(update(id, "title", title));
  };

  const handleSampleChange = id => event => {
    const checked = event.target.checked;
    setChapters(update(id, "isSample", checked ? 1 : 0));
  };

  const updateContent = useContentUpdate();
  const updateContentFiles = useUpdateContentFiles();
  const handleSave = useCallback(async () => {
    try {
      if (isAudiobook(context.media)) {
        await updateContentFiles.mutateAsync({
          resourceIds: { id: context.media.id },
          data: chapters,
        });
      }

      if (anyPass([isAlbum, isChapterBook])(context.media)) {
        await Promise.all(
          map(async chapter => {
            const initialChapter = find(
              propEq("id", chapter.id),
              initialChapters,
            );
            const update = pickBy(objectDiff(initialChapter), chapter);
            if (notBlank(update)) {
              await updateContent.mutateAsync({
                resourceIds: { id: chapter.id },
                data: update,
              });
            }
          }, chapters),
        );
      }

      const message = isAlbum(context.media) ? "Tracks" : "Chapters";
      showAlert("success", [`${message} Updated`]);
    } catch (err) {
      showAlert("error", [err.message]);
    }
  }, [
    chapters,
    context.media,
    initialChapters,
    showAlert,
    updateContent,
    updateContentFiles,
  ]);

  //-- Manage Drag and Drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = event => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const mapper = isAudiobook(context.media) ? mapSequences : mapChildOrder;
      setChapters(reorderItems({ active, over, mapper: mapper }));
    }
  };

  return (
    <Dialog maxWidth={false} open={open} onClose={onClose}>
      {files.isLoading ? (
        <PanelLoadingIndicator />
      ) : (
        <DndContext
          onDragEnd={handleDragEnd}
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[
            restrictToVerticalAxis,
            restrictToFirstScrollableAncestor,
          ]}
        >
          <SortableContext
            items={chapters}
            strategy={verticalListSortingStrategy}
          >
            <DialogTitle
              id="edit-chapters-dialog-title"
              disableTypography
              className={classes.dialogTitle}
            >
              <Grid
                container
                alignItems="center"
                justify="space-between"
                style={{ paddingBottom: 24 }}
              >
                <Grid item>
                  <Typography variant="h6">
                    {isAlbum(context.media) ? "Tracks" : "Chapters"}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <CloseIcon className={classes.closeButtonIcon} />
                  </IconButton>
                </Grid>
              </Grid>

              <Grid container>
                <Grid
                  container
                  item
                  justify="space-between"
                  alignItems="center"
                >
                  <Grid item>
                    <Typography style={{ paddingLeft: 42 }}>Title</Typography>
                  </Grid>

                  {isAudiobook(context.media) && (
                    <Grid item>
                      <Typography style={{ paddingRight: 20 }}>
                        Length
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Divider className={classes.divider} />
            </DialogTitle>

            <DialogContent>
              <SnackAlert handler={snackAlertHandler} />

              {files.isSuccess && notBlank(chapters) && (
                <Grid container>
                  {pipe(
                    isAudiobook(context.media) ? tail : identity,
                    sort(
                      ascend(
                        prop(
                          isAudiobook(context.media)
                            ? "sequence"
                            : "childOrder",
                        ),
                      ),
                    ),
                    map(chapter => (
                      <SortableItem
                        key={chapter.id}
                        id={chapter.id}
                        renderChild={({ ...props }) => {
                          return isAudiobook(context.media) ? (
                            <AudiobookChapterRow
                              {...props}
                              chapter={chapter}
                              handleTitleChange={handleTitleChange}
                              handleSampleChange={handleSampleChange}
                            />
                          ) : (
                            <BookChapterRow
                              {...props}
                              chapter={chapter}
                              handleTitleChange={handleTitleChange}
                            />
                          );
                        }}
                      />
                    )),
                  )(chapters)}
                </Grid>
              )}
            </DialogContent>

            <Divider style={{ margin: "0 22px" }} />

            <DialogActions className={classes.dialogActions}>
              <Button onClick={handleCancel}>Cancel</Button>
              <SaveButton
                loading={
                  updateContent.isLoading || updateContentFiles.isLoading
                }
                onClick={handleSave}
                disabled={equals(chapters, initialChapters)}
              />
            </DialogActions>
          </SortableContext>
        </DndContext>
      )}
    </Dialog>
  );
});

export default EditChaptersDialog;
